'use client'

import React, { useState } from 'react'
import { useTheme } from '../theme/provider'
import { ArticleCard, Article } from './article-card'
import { Breadcrumb, BreadcrumbItem } from './breadcrumb'

export interface CategoryFilter {
  id: string
  label: string
  slug: string
}

export interface CategoryPageProps {
  /**
   * Page title
   */
  title: string
  /**
   * Page description
   */
  description?: string
  /**
   * Breadcrumb items for navigation
   */
  breadcrumbItems: BreadcrumbItem[]
  /**
   * Articles to display
   */
  articles: Article[]
  /**
   * Category filters
   */
  categories: CategoryFilter[]
  /**
   * Active category ID
   */
  activeCategoryId?: string
  /**
   * Function to get article URL
   */
  getArticleUrl: (article: Article) => string
  /**
   * Optional: Function to optimize image URLs
   */
  getOptimizedImageUrl?: (url: string, width?: number, quality?: number) => string
  /**
   * Optional: Function to get category label
   */
  getCategoryLabel?: (article: Article, fallbackIndex?: number) => string
  /**
   * Optional: Function to format date
   */
  formatDate?: (date: string) => string
  /**
   * Optional: Function to strip HTML
   */
  stripHtml?: (html: string) => string
  /**
   * Locale for localization
   */
  locale?: string
  /**
   * On category change handler
   */
  onCategoryChange?: (categoryId: string) => void
  /**
   * On load more click handler
   */
  onLoadMore?: () => void
  /**
   * Loading state for load more
   */
  loadingMore?: boolean
  /**
   * Show load more button
   */
  hasMore?: boolean
  /**
   * Base URL for breadcrumb schema
   */
  baseUrl?: string
}

/**
 * CategoryPage Template - Real Madrid Style
 *
 * Article listing page with filters and grid layout:
 * - Page header with title and description
 * - Breadcrumb navigation
 * - Category filter bar (horizontal buttons)
 * - Article count display
 * - Responsive article grid (3 cols desktop, 2 tablet, 1 mobile)
 * - Load more button with pagination
 * - Max width container: 1440px
 *
 * NOTE: This component should be wrapped with PageLayout component to ensure
 * consistent header/footer spacing across all pages.
 *
 * @example
 * ```tsx
 * <PageLayout header={headerProps} footer={footerProps}>
 *   <CategoryPage
 *     title="Latest News"
 *     articles={articles}
 *     categories={categories}
 *     breadcrumbItems={breadcrumbs}
 *     getArticleUrl={(article) => `/news/${article.id}`}
 *     locale="en"
 *   />
 * </PageLayout>
 * ```
 */
export function CategoryPage({
  title,
  description,
  breadcrumbItems,
  articles,
  categories,
  activeCategoryId,
  getArticleUrl,
  getOptimizedImageUrl,
  getCategoryLabel,
  formatDate,
  stripHtml,
  locale = 'en',
  onCategoryChange,
  onLoadMore,
  loadingMore = false,
  hasMore = true,
  baseUrl,
}: CategoryPageProps) {
  const { theme } = useTheme()
  const [activeCategory, setActiveCategory] = useState(activeCategoryId || 'all')

  const handleCategoryClick = (categoryId: string) => {
    setActiveCategory(categoryId)
    onCategoryChange?.(categoryId)
  }

  return (
    <div
      className="pb-16 transition-colors duration-300"
      style={{ backgroundColor: theme.colors.background }}
    >
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb Navigation */}
        <div className="mb-6">
          <Breadcrumb
            items={breadcrumbItems}
            locale={locale}
            baseUrl={baseUrl}
          />
        </div>

        {/* Page Header */}
        <header className="mb-8 text-center">
          <h1
            className="text-4xl md:text-5xl font-bold mb-4"
            style={{ color: theme.colors.text }}
          >
            {title}
          </h1>
          {description && (
            <p
              className="text-lg md:text-xl max-w-3xl mx-auto"
              style={{ color: theme.colors.textSecondary }}
            >
              {description}
            </p>
          )}
        </header>

        {/* Category Filter Bar */}
        <div className="mb-8">
          <div
            className="flex flex-wrap gap-3 justify-center p-4 rounded-xl"
            style={{
              backgroundColor: theme.colors.surface,
              borderColor: theme.colors.border,
            }}
          >
            {categories.map((category) => {
              const isActive = activeCategory === category.id
              return (
                <button
                  key={category.id}
                  onClick={() => handleCategoryClick(category.id)}
                  className="px-5 py-2.5 rounded-lg font-semibold text-sm uppercase tracking-wide transition-all duration-300 hover:shadow-md"
                  style={{
                    backgroundColor: isActive ? theme.colors.primary : theme.colors.background,
                    color: isActive ? '#ffffff' : theme.colors.text,
                    border: `2px solid ${isActive ? theme.colors.primary : theme.colors.border}`,
                  }}
                >
                  {category.label}
                </button>
              )
            })}
          </div>
        </div>

        {/* Article Count */}
        <div className="mb-6">
          <p
            className="text-sm font-semibold"
            style={{ color: theme.colors.textSecondary }}
          >
            {articles.length} {articles.length === 1 ? 'article' : 'articles'}
          </p>
        </div>

        {/* Articles Grid */}
        {articles.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {articles.map((article, index) => (
                <ArticleCard
                  key={article.id}
                  article={article}
                  index={index}
                  getArticleUrl={getArticleUrl}
                  getOptimizedImageUrl={getOptimizedImageUrl}
                  getCategoryLabel={getCategoryLabel}
                  formatDate={formatDate}
                  stripHtml={stripHtml}
                  loading={index < 6 ? 'eager' : 'lazy'}
                  priority={index < 3}
                  locale={locale}
                />
              ))}
            </div>

            {/* Load More Button */}
            {hasMore && onLoadMore && (
              <div className="flex justify-center mt-12">
                <button
                  onClick={onLoadMore}
                  disabled={loadingMore}
                  className="px-8 py-3 rounded-lg font-semibold transition-all duration-300 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: theme.colors.primary,
                    color: '#ffffff',
                  }}
                >
                  {loadingMore ? 'Loading...' : 'Load More Articles'}
                </button>
              </div>
            )}
          </>
        ) : (
          /* Empty State */
          <div
            className="text-center py-16 rounded-xl"
            style={{
              backgroundColor: theme.colors.surface,
              borderColor: theme.colors.border,
            }}
          >
            <div className="mb-4">
              <svg
                className="w-16 h-16 mx-auto opacity-50"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                style={{ color: theme.colors.textSecondary }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h3
              className="text-xl font-bold mb-2"
              style={{ color: theme.colors.text }}
            >
              No articles found
            </h3>
            <p style={{ color: theme.colors.textSecondary }}>
              Try selecting a different category
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
